const NGINX_DEFAULT_MAX_BODY_SIZE_BYTES = 1024 * 1024;
const AUDIO_MAX_BODY_SIZE_BYTES = 16 * 1024 * 1024;
const SUPPORTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Falha ao ler ${file.name}.`));
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Falha ao processar imagem."));
    image.src = src;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Falha ao gerar arquivo compactado."));
        return;
      }
      resolve(blob);
    }, type, quality);
  });

const fileNameWithoutExtension = (fileName = "") =>
  fileName.replace(/\.[^.]+$/, "");

export const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const compressImageToLimit = async (
  file,
  maxBytes = NGINX_DEFAULT_MAX_BODY_SIZE_BYTES
) => {
  const dataUrl = await readFileAsDataUrl(file);
  const image = await loadImage(dataUrl);
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Canvas indisponivel para reduzir imagem.");
  }

  let width = image.width;
  let height = image.height;
  let quality = file.type === "image/png" ? undefined : 0.9;
  const outputType = file.type === "image/png" ? "image/jpeg" : file.type;
  const extension = outputType === "image/jpeg" ? "jpg" : outputType.split("/")[1];

  for (let attempt = 0; attempt < 8; attempt += 1) {
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await canvasToBlob(canvas, outputType, quality);
    if (blob.size <= maxBytes) {
      return new File([blob], `${fileNameWithoutExtension(file.name)}.${extension}`, {
        type: outputType,
      });
    }

    width *= 0.85;
    height *= 0.85;
    if (typeof quality === "number") {
      quality = Math.max(0.45, quality - 0.1);
    }
  }

  throw new Error(
    `Nao foi possivel reduzir ${file.name} para menos de ${formatBytes(maxBytes)}.`
  );
};

export const normalizeFileForUpload = async (
  file,
  maxBytes = NGINX_DEFAULT_MAX_BODY_SIZE_BYTES
) => {
  const effectiveMaxBytes = file.type?.startsWith("audio/")
    ? Math.max(maxBytes, AUDIO_MAX_BODY_SIZE_BYTES)
    : maxBytes;

  if (file.size <= effectiveMaxBytes) {
    return file;
  }

  if (SUPPORTED_IMAGE_TYPES.includes(file.type)) {
    return compressImageToLimit(file, effectiveMaxBytes);
  }

  throw new Error(
    `${file.name} excede ${formatBytes(effectiveMaxBytes)} e nao pode ser reduzido automaticamente.`
  );
};

export const normalizeFilesForUpload = async (
  files,
  maxBytes = NGINX_DEFAULT_MAX_BODY_SIZE_BYTES
) => Promise.all(files.map((file) => normalizeFileForUpload(file, maxBytes)));

export { NGINX_DEFAULT_MAX_BODY_SIZE_BYTES };
export { AUDIO_MAX_BODY_SIZE_BYTES };
